import { useState, useEffect } from 'react'
import { LockOutlined } from '@ant-design/icons'

import { Trans, useTranslation } from 'next-i18next'

import { DifficultyStats } from '~/statistics-v2/types/statistics.types'
import { Link } from '~/components/Link'

export default function ActivityProgressCard({
  props,
  subTopicPath,
}: {
  props: DifficultyStats
  subTopicPath: string
}): React.ReactElement {
  const { t } = useTranslation('statisticsV2')
  const [isMounted, setIsMounted] = useState(false)

  const getBarColor = (progress: number, bestPerformance: number): string => {
    if (progress >= 100 && progress === bestPerformance) return 'bg-green-lime'
    if (progress === bestPerformance) return 'bg-blue-pastel'
    return 'bg-blue-blueberry'
  }

  useEffect(() => {
    const timer = setTimeout(() => setIsMounted(true), 50)
    return () => clearTimeout(timer)
  }, [])

  const rawHistory = [...(props.history ?? []), { progressPercentage: props.progressPercentage ?? 0 }].slice(-5)

  const firstNonZeroIndex = rawHistory.findIndex((item) => (item.progressPercentage ?? 0) > 0)

  const isLocked = !props.isComplete && firstNonZeroIndex === -1

  const displayHistory =
    firstNonZeroIndex === -1
      ? rawHistory.length > 0
        ? [{ progressPercentage: 0 }]
        : []
      : rawHistory.slice(firstNonZeroIndex)

  const bestPerformanceValue = Math.max(
    props.progressPercentage ?? 0,
    ...(props.history ?? []).map((h) => h.progressPercentage ?? 0),
  )

  return (
    <div
      className={`w-full max-w-67 h-67.5 p-5 rounded-4 bg-purple-darker shadow-2xl border border-white/5 transition-all duration-700 ease-out transform flex flex-col justify-between relative ${
        !isLocked ? 'hover:border-white/20' : 'bg-opacity-60 cursor-not-allowed'
      } ${isMounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
    >
      {isLocked && (
        <div className="absolute top-4 right-4 z-10">
          <LockOutlined className="w-5 h-5 text-gray opacity-60" />
        </div>
      )}

      <div className={isLocked ? 'opacity-60' : ''}>
        <h3 className="text-blue-pastel font-bold text-xxs mb-3 uppercase">
          {t('part', { n: props.index !== undefined ? props.index + 1 : 1 })}
        </h3>

        <div className="flex items-baseline gap-2 mb-0.5">
          <h1 className="text-3xl font-black text-white tracking-tight">
            {Math.round(props.progressPercentage ?? 0)}%
          </h1>
          <span className="text-blue-pastel text-xs font-semibold opacity-80">{t('accuracy')}</span>
        </div>

        <p className="text-blue-pastel text-2.5 font-medium mb-4">
          {Trans({
            t,
            i18nKey: 'bestPerformance',
            components: {
              span: <span className={` ${bestPerformanceValue === 100 ? 'text-green-lime' : 'text-blue-pastel'}`} />,
            },
            children: t('bestPerformance'),
            values: { performance: Math.round(bestPerformanceValue) },
          })}
        </p>

        <div className="mb-4">
          <h4 className="text-blue-pastel font-bold text-2.5 mb-3 uppercase">{t(`history${displayHistory.length}`)}</h4>

          <div
            className={`flex items-end gap-1.5 h-10 ${
              displayHistory.length < 5 ? 'justify-center' : 'justify-between'
            }`}
          >
            {displayHistory.map((item, index) => {
              const isLast = index === displayHistory.length - 1
              const barColor = getBarColor(item.progressPercentage ?? 0, bestPerformanceValue)

              return (
                <div className="w-8 h-full flex flex-col justify-end" key={item.progressPercentage}>
                  <div
                    className={`w-full rounded-t-1 ${barColor} relative transition-all duration-700 ease-out origin-bottom ${
                      isLast ? 'shadow-[0_0_10px_theme(colors.green.lime/50%)]' : ''
                    }`}
                    style={{
                      height: isMounted ? `${item.progressPercentage ?? 0}%` : '0%',
                      transitionDelay: `${0.2 + index * 0.1}s`,
                    }}
                  >
                    {isLast && <div className="absolute inset-0 bg-white/20 blur-sm rounded-sm" />}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <Link
        className={`flex justify-center w-full text-white text-xs font-bold py-2.5 rounded shadow-lg transition-all uppercase tracking-widest ${'bg-purple hover:bg-purple-500 active:scale-95 opacity-100'}`}
        to={subTopicPath}
      >
        {t('goToLevels')}
      </Link>
    </div>
  )
}
